'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import * as prismic from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';

export type HighlightWithImageAndTextCtaProps =
  SliceComponentProps<Content.HighlightWithImageAndTextCtaSlice>;

const HighlightWithImageAndTextCta: FC<HighlightWithImageAndTextCtaProps> = ({
  slice,
}) => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) {
      return;
    }

    const targets = el.querySelectorAll<HTMLElement>('[data-animate]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            target.style.opacity = '1';
            target.style.transform = 'translateY(0)';
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.15 },
    );

    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  const { label, heading, description, primary_cta, main_image } =
    slice.primary;

  // Use green badge images — apply grayscale filter by default, remove on hover
  type GreenBadgeItem = {
    badge_image: prismic.ImageField<never>;
    id_label: prismic.KeyTextField;
  };

  const greenBadges = ((
    slice.primary as {
      badges_certifications_green?: GreenBadgeItem[];
    }
  ).badges_certifications_green ?? []) as GreenBadgeItem[];

  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-[30px] overflow-hidden rounded-[24px] bg-white px-6 py-[60px] md:px-[80px] lg:px-[150px] lg:py-[80px]"
    >
      {/* Content row */}
      <div className="flex flex-col items-center gap-10 lg:flex-row lg:items-center lg:gap-[80px]">
        {/* Left column */}
        <div
          className="flex w-full flex-col items-start gap-[24px] lg:w-[647px] lg:shrink-0"
          data-animate
          style={{
            opacity: 0,
            transform: 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          {/* Eyebrow pill */}
          {label && (
            <div className="inline-flex rounded-[89px] border border-[#4b0fea] px-[16px] py-[8px]">
              <span className="text-center text-[14px] uppercase leading-[1.2] text-[#041118]">
                {label}
              </span>
            </div>
          )}

          {/* Heading with purple highlights on <strong> */}
          {isFilled.richText(heading) && (
            <PrismicRichText
              field={heading}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[24px] font-semibold leading-[1.3] text-[#041118] md:text-[28px] lg:text-[32px]">
                    {children}
                  </h2>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-[#4b0fea]">
                    {children}
                  </strong>
                ),
              }}
            />
          )}

          {/* Description */}
          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-[16px] font-light leading-[1.5] text-[#041118]">
                    {children}
                  </p>
                ),
              }}
            />
          )}

          {/* CTA button */}
          {isFilled.link(primary_cta) && (
            <PrismicNextLink
              field={primary_cta}
              className="inline-flex items-center justify-center rounded-[100px] bg-[#00f557] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#041118] transition-opacity hover:opacity-90"
            >
              {primary_cta.text || 'Learn More'}
            </PrismicNextLink>
          )}
        </div>

        {/* Right column - main image */}
        {isFilled.image(main_image) && (
          <div
            className="relative flex-1"
            data-animate
            style={{
              opacity: 0,
              transform: 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
            }}
          >
            <PrismicNextImage
              field={main_image}
              className="h-auto w-full object-contain"
            />
          </div>
        )}
      </div>

      {/* Badge bar — breaks out of parent padding to span full container width */}
      {greenBadges.length > 0 && (
        <div
          className="relative mt-[60px] -mx-6 md:-mx-[80px] lg:-mx-[150px] flex items-center justify-center gap-[24px] px-6 py-[30px] md:px-[40px] lg:gap-[40px] lg:px-[60px]"
          data-animate
          style={{
            opacity: 0,
            transform: 'translateY(24px)',
            transition: 'opacity 0.6s ease 0.4s, transform 0.6s ease 0.4s',
          }}
        >
          {/* Top fading line */}
          <div
            className="pointer-events-none absolute left-0 right-0 top-0 h-px"
            style={{
              background:
                'linear-gradient(to right, transparent, #eaeaea 20%, #eaeaea 80%, transparent)',
            }}
          />
          {/* Bottom fading line */}
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-px"
            style={{
              background:
                'linear-gradient(to right, transparent, #eaeaea 20%, #eaeaea 80%, transparent)',
            }}
          />
          {greenBadges.map((badge, i) =>
            isFilled.image(badge.badge_image) ? (
              <PrismicNextImage
                key={i}
                field={badge.badge_image}
                className="h-[34px] w-auto object-contain grayscale opacity-60 transition-all duration-300 hover:grayscale-0 hover:opacity-100 lg:h-[44px]"
              />
            ) : null,
          )}
        </div>
      )}
    </section>
  );
};

export default HighlightWithImageAndTextCta;
